import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  const onSubmit = async (data) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Message envoyé:', data);
    alert(t('contact_success'));
    reset();
  };

  const contactInfo = [
    {
      icon: '📍',
      title: t('contact_main_agency'),
      desc: "Dembe\nRue des Transports, Porte 123",
      color: 'from-red-500 to-red-400',
      details: ['📌 ' + t('contact_landmark'), '🅿️ ' + t('contact_parking')]
    },
    {
      icon: '📱',
      title: t('contact_phones'),
      desc: '+235 64 64 51 51',
      color: 'from-green-500 to-green-400',
      details: ['📲 WhatsApp: +235 98 22 21 10', '⏰ Lun-Dim: 06h00-22h00']
    },
    {
      icon: '⏰',
      title: t('contact_hours'),
      desc: 'Lundi-Dimanche: 06h00-22h00',
      color: 'from-blue-500 to-blue-400',
      details: ['🎫 ' + t('contact_24h_booking'), '🚍 ' + t('contact_early_departures')]
    },
    {
      icon: '✉️',
      title: t('contact_email'),
      desc: 'contact@expressabouhamama.td\nsupport@expressabouhamama.td',
      color: 'from-purple-500 to-purple-400',
      details: ['📧 ' + t('contact_response_time'), '📱 ' + t('contact_sms_followup')]
    }
  ];

  return (
    <div className="bg-neutral-50 py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* EN-TÊTE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-display font-bold text-neutral-900 mb-4">
            📞 {t('contact_title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('contact_subtitle')}
          </p>
        </motion.div>

        {/* GRILLE DE COORDONNÉES */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition"
            >
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${info.color} text-white flex items-center justify-center text-2xl mb-4`}>
                {info.icon}
              </div>
              <h3 className="font-display font-bold text-xl text-neutral-900 mb-2">
                {info.title}
              </h3>
              <p className="text-gray-600 mb-3 whitespace-pre-line">{info.desc}</p>
              <div className="text-xs text-gray-500 space-y-1">
                {info.details.map((detail, idx) => (
                  <p key={idx}>{detail}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* FORMULAIRE ET CARTE */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulaire */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-8 shadow-2xl"
          >
            <h2 className="text-2xl font-display font-bold mb-6 text-neutral-900">
              ✍️ {t('contact_form_title')}
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Nom complet */}
              <div>
                <label className="block font-semibold text-neutral-900 mb-2">
                  {t('contact_name')} *
                </label>
                <input
                  {...register("name", { 
                    required: t('contact_name_required'),
                    minLength: { value: 3, message: t('contact_name_min') }
                  })}
                  className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                  placeholder={t('contact_name_placeholder')}
                />
                {errors.name && <span className="text-red-500 text-sm block mt-1">{errors.name.message}</span>}
              </div>

              {/* Téléphone */}
              <div>
                <label className="block font-semibold text-neutral-900 mb-2">
                  {t('contact_phone')} *
                </label>
                <input
                  {...register("phone", { 
                    required: t('contact_phone_required'),
                    pattern: { 
                      value: /^(\+235|00235)?\s?[67]\d{7}$/, 
                      message: t('contact_phone_invalid') 
                    }
                  })}
                  className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                  placeholder="+235 66 12 34 56"
                />
                {errors.phone && <span className="text-red-500 text-sm block mt-1">{errors.phone.message}</span>}
              </div>

              {/* Email */}
              <div>
                <label className="block font-semibold text-neutral-900 mb-2">
                  {t('contact_email')}
                </label>
                <input
                  type="email"
                  {...register("email", { 
                    pattern: { 
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('contact_email_invalid')
                    }
                  })}
                  className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                  placeholder="contact@example.com"
                />
                {errors.email && <span className="text-red-500 text-sm block mt-1">{errors.email.message}</span>}
              </div>

              {/* Objet */}
              <div>
                <label className="block font-semibold text-neutral-900 mb-2">
                  {t('contact_subject')} *
                </label>
                <select
                  {...register("subject", { required: t('contact_subject_required') })}
                  className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition"
                >
                  <option value="">{t('contact_subject_select')}</option>
                  <option value="booking">{t('contact_subject_booking')}</option>
                  <option value="info">{t('contact_subject_info')}</option>
                  <option value="complaint">{t('contact_subject_complaint')}</option>
                  <option value="other">{t('contact_subject_other')}</option>
                </select>
                {errors.subject && <span className="text-red-500 text-sm block mt-1">{errors.subject.message}</span>}
              </div>

              {/* Message */}
              <div>
                <label className="block font-semibold text-neutral-900 mb-2">
                  {t('contact_message')} *
                </label>
                <textarea
                  {...register("message", { 
                    required: t('contact_message_required'),
                    minLength: { value: 10, message: t('contact_message_min') }
                  })}
                  className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition h-32"
                  placeholder={t('contact_message_placeholder')}
                  rows="5"
                />
                {errors.message && <span className="text-red-500 text-sm block mt-1">{errors.message.message}</span>}
              </div>

              {/* Bouton envoyer */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white font-bold p-4 rounded-xl hover:from-primary-700 hover:to-primary-800 transition transform hover:scale-105 disabled:opacity-50"
              >
                {isSubmitting ? '⏳ ' + t('contact_sending') : '📨 ' + t('contact_send')}
              </button>
            </form>
          </motion.div>

          {/* Carte Google Maps */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-4 shadow-2xl h-96"
          >
            <h2 className="text-2xl font-display font-bold mb-4 text-neutral-900">
              🗺️ {t('contact_find_us')}
            </h2>
            <div className="w-full h-full rounded-xl overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1890.661880851788!2d15.083017895747325!3d12.103565019424027!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1119616db1d57e6f%3A0x28ec1c8df5e65470!2sExpress%20Abou%20Hamama!5e0!3m2!1sfr!2std!4v1766224032490!5m2!1sfr!2std" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Carte Express Abou Hamama Dembe"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}